import './carregando-triplice.css';

import marca from '@/assets/marca.png';

import { cn } from '@/lib/utils';

type Tamanho = 'pequeno' | 'medio' | 'grande';

interface CarregandoTriplicePropriedades {
  /** `pequeno` em cartão, `medio` em painel, `grande` em tela cheia. */
  tamanho?: Tamanho;
  /** Texto visível e anunciado pelo leitor de tela. */
  rotulo?: string;
  className?: string;
}

/**
 * As três mentes do logotipo acendendo uma a uma, em ciclo — a assinatura da
 * marca virada em indicador de espera.
 *
 * Quando usar (ver `docs/CONVENCOES-UI.md`): só onde não há forma a prever —
 * abertura do app, `lazy` de rota, cartão cujo conteúdo é um número ou um
 * gráfico. Tabela e lista continuam com `Skeleton`, que mostra a forma do que
 * vem e não dá pulo de layout.
 *
 * O `role="status"` com `aria-live="polite"` anuncia a espera sem roubar o
 * foco de quem está navegando por teclado; a arte e os halos são decoração e
 * saem da árvore de acessibilidade.
 */
export function CarregandoTriplice({
  tamanho = 'medio',
  rotulo = 'Carregando…',
  className,
}: CarregandoTriplicePropriedades) {
  return (
    <div
      role="status"
      aria-live="polite"
      className={cn('carregando', `carregando--${tamanho}`, className)}
    >
      <div className="carregando__marca" aria-hidden="true">
        <img className="carregando__simbolo" src={marca} width={760} height={509} alt="" />
        <span className="carregando__mente carregando__mente--1" />
        <span className="carregando__mente carregando__mente--2" />
        <span className="carregando__mente carregando__mente--3" />
      </div>
      <span className="carregando__rotulo">{rotulo}</span>
    </div>
  );
}

/**
 * O mesmo, centrado numa cortina grafite que cobre a tela. Para a abertura do
 * app, a checagem de sessão e o `fallback` do `lazy` das rotas.
 */
export function CarregandoTelaCheia({ rotulo }: Pick<CarregandoTriplicePropriedades, 'rotulo'>) {
  return (
    <div className="carregando-tela">
      <CarregandoTriplice tamanho="grande" rotulo={rotulo} />
    </div>
  );
}

/**
 * Versão de dentro de botão: três pontos em triângulo, no mesmo ritmo, sem a
 * arte (num botão a marca não se lê e o PNG seria peso jogado fora). Herda a
 * cor do botão por `currentColor`.
 *
 * O rótulo do próprio botão já muda para "Salvando…", então aqui o texto é só
 * para leitor de tela.
 */
export function CarregandoBotao() {
  return (
    <span className="carregando-botao" role="status" aria-live="polite">
      <span className="carregando-botao__ponto carregando-botao__ponto--1" aria-hidden="true" />
      <span className="carregando-botao__ponto carregando-botao__ponto--2" aria-hidden="true" />
      <span className="carregando-botao__ponto carregando-botao__ponto--3" aria-hidden="true" />
      <span className="sr-only">Carregando…</span>
    </span>
  );
}
